/**
 * Stratégie de merge des sauvegardes, MIROIR de `server/src/sync/merge.js`.
 * Garder les deux strictement alignés (mêmes règles → convergence client/serveur).
 *
 * Une « snapshot » est un objet plat { cléLocalStorage: valeurString }.
 */

const DAILY_RESULT_RE = /^geonerd\.daily(?:\.hard)?\.\d{4}-\d{2}-\d{2}$/;
const STREAK_RE = /^geonerd\.daily(?:\.hard)?\.streak$/;
const COUNTRY_PROGRESS_RE = /^(?:fr|en)\.countrynerd(?:hard)?\.progress\.[a-z]$/;
const COUNTRY_LETTER_RE = /^(?:fr|en)\.countrynerd(?:hard)?\.letter\.[a-z]$/;
// Résultat consolidé d'une lettre terminée : { time, errors }.
const COUNTRY_RUN_RE = /^(?:fr|en)\.countrynerd(?:hard)?\.letterRun\.[a-z]$/;
export const EXPLORATION_KEY = "geonerd.mapexploration.discovered";
const NUMERIC_SUFFIXES = [".best", ".gamesPlayed", ".cumulativeScore"];
const BEST_RUN_RE = /\.bestRun$/;
// Meilleure partie de la semaine (classements hebdomadaires).
const WEEK_RUN_RE = /\.weekRun$/;
// Passeport : trois tampons par pays (drapeau, capitale, carte), en bits.
export const PASSPORT_KEY = "geonerd.passport";
// Révision espacée des erreurs : boîte de Leitner et échéance par pays.
export const REVIEW_KEY = "geonerd.review";

export function isNumericKey(key) {
    return NUMERIC_SUFFIXES.some((s) => key.endsWith(s));
}
function isExplorationKey(key) {
    return key === EXPLORATION_KEY;
}
function isDailyResultKey(key) {
    return DAILY_RESULT_RE.test(key);
}
function isStreakKey(key) {
    return STREAK_RE.test(key);
}
function isCountryProgressKey(key) {
    return COUNTRY_PROGRESS_RE.test(key);
}
function isLetterRunKey(key) {
    return COUNTRY_RUN_RE.test(key);
}

function isLetterFlagKey(key) {
    return COUNTRY_LETTER_RE.test(key);
}

function isBestRunKey(key) {
    return BEST_RUN_RE.test(key);
}

function isWeekRunKey(key) {
    return WEEK_RUN_RE.test(key);
}
function isPassportKey(key) {
    return key === PASSPORT_KEY;
}
function isReviewKey(key) {
    return key === REVIEW_KEY;
}

export function isSyncableKey(key) {
    return (
        isNumericKey(key) ||
        isExplorationKey(key) ||
        isDailyResultKey(key) ||
        isStreakKey(key) ||
        isCountryProgressKey(key) ||
        isLetterFlagKey(key) ||
        isLetterRunKey(key) ||
        isBestRunKey(key) ||
        isWeekRunKey(key) ||
        isPassportKey(key) ||
        isReviewKey(key)
    );
}

function parseJson(value, fallback) {
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

/** Chrono forcé (59:59) d'un run dont la durée est corrompue. */
export const ABERRANT_DURATION = 3599;
/**
 * Plancher de plausibilité. Les meilleurs runs réels tournent autour de 3 s par
 * point ; en dessous d'une demi-seconde par point le chrono est cassé (ou forgé),
 * pas performant. Volontairement très bas pour ne jamais pénaliser un vrai joueur.
 */
const MIN_SECONDS_PER_POINT = 0.5;

/**
 * Neutralise le chrono d'un run aberrant en le portant à 59:59, de sorte qu'il ne
 * puisse plus se glisser en tête d'un classement départagé au temps (un `duration: 0`
 * battait mécaniquement tout le monde à score et erreurs égaux).
 *
 * `duration: null` reste valide : c'est le cas légitime d'un mode sans chrono
 * (SpeedRun) ou d'un run enregistré avant l'introduction du chrono.
 *
 * @param {object|null} run
 * @returns {object|null} le run tel quel, ou une copie au chrono neutralisé
 */
export function sanitizeRun(run) {
    if (!run || typeof run !== "object") return run;
    const { duration, score } = run;
    if (duration === null || duration === undefined) return run;
    const aberrant =
        typeof duration !== "number" ||
        !Number.isFinite(duration) ||
        duration <= 0 ||
        (typeof score === "number" && duration < score * MIN_SECONDS_PER_POINT);
    return aberrant ? { ...run, duration: ABERRANT_DURATION } : run;
}

function mergeNumeric(a, b) {
    return String(Math.max(parseInt(a, 10) || 0, parseInt(b, 10) || 0));
}
function mergeExploration(a, b) {
    return JSON.stringify([...new Set([...parseJson(a, []), ...parseJson(b, [])])]);
}
function mergeDailyResult(a, b) {
    const ra = parseJson(a, null);
    const rb = parseJson(b, null);
    if (!ra) return b;
    if (!rb) return a;
    return (rb.score || 0) > (ra.score || 0) ? b : a;
}
function mergeStreak(a, b) {
    const sa = parseJson(a, null);
    const sb = parseJson(b, null);
    if (!sa) return b;
    if (!sb) return a;
    const longest = Math.max(sa.longest || 0, sb.longest || 0);
    const recent = (sb.lastPlayedDay || "") > (sa.lastPlayedDay || "") ? sb : sa;
    return JSON.stringify({ ...recent, longest });
}

function mergeCountryProgress(a, b) {
    return JSON.stringify([...new Set([...parseJson(a, []), ...parseJson(b, [])])]);
}
function mergeLetterFlag(a, b) {
    return a === "true" || b === "true" ? "true" : a || b;
}

/**
 * Résultat d'une lettre de Country Nerd (`{ time, errors }`) : moins de fautes
 * d'abord, puis moins de temps. `time: null` (lettre commencée avant que la mesure
 * n'existe) est toujours le moins bon. Partagé avec le jeu, qui s'en sert pour ne
 * remplacer un résultat que par un meilleur.
 */
export function isBetterLetterRun(a, b) {
    if (!b) return true;
    if (!a) return false;
    const ea = a.errors ?? Infinity, eb = b.errors ?? Infinity;
    if (ea !== eb) return ea < eb;
    return (a.time ?? Infinity) < (b.time ?? Infinity);
}

function mergeLetterRun(a, b) {
    const ra = parseJson(a, null);
    const rb = parseJson(b, null);
    if (!ra) return b;
    if (!rb) return a;
    return isBetterLetterRun(rb, ra) ? b : a;
}

/**
 * Le run gagnant est réécrit (et pas seulement choisi) : la sanitisation du chrono
 * est ainsi persistée dès la première synchro, y compris quand un seul des deux
 * côtés possède la clé.
 */
function mergeBestRun(a, b) {
    const ra = sanitizeRun(parseJson(a, null));
    const rb = sanitizeRun(parseJson(b, null));
    if (!ra && !rb) return a ?? b;
    if (!ra) return JSON.stringify(rb);
    if (!rb) return JSON.stringify(ra);
    if (rb.score !== ra.score) return JSON.stringify(rb.score > ra.score ? rb : ra);
    const ea = ra.errors ?? Infinity;
    const eb = rb.errors ?? Infinity;
    if (ea !== eb) return JSON.stringify(ea < eb ? ra : rb);
    const da = ra.duration ?? Infinity;
    const db = rb.duration ?? Infinity;
    return JSON.stringify(da <= db ? ra : rb);
}

/**
 * Meilleure partie de la semaine (`{ week, score, errors, duration, recordedAt }`) :
 * la semaine la plus récente l'emporte (une ancienne ne compte plus), puis, à
 * semaine égale, le meilleur run dans le même ordre que `bestRun`.
 */
function mergeWeekRun(a, b) {
    const ra = sanitizeRun(parseJson(a, null));
    const rb = sanitizeRun(parseJson(b, null));
    if (!ra && !rb) return a ?? b;
    if (!ra) return JSON.stringify(rb);
    if (!rb) return JSON.stringify(ra);
    if (ra.week !== rb.week) return JSON.stringify((rb.week || "") > (ra.week || "") ? rb : ra);
    return mergeBestRun(JSON.stringify(ra), JSON.stringify(rb));
}

/** Passeport `{ code: bits }` : un tampon gagné sur un appareil l'est partout (OU bit à bit). */
function mergePassport(a, b) {
    const pa = parseJson(a, {}) || {};
    const pb = parseJson(b, {}) || {};
    const out = {};
    for (const code of [...new Set([...Object.keys(pa), ...Object.keys(pb)])].sort()) {
        out[code] = (Number(pa[code]) | 0) | (Number(pb[code]) | 0);
    }
    return JSON.stringify(out);
}

/**
 * Révision `{ compétence: { code: { box, due, t } } }` : par pays, l'entrée la plus
 * récemment mise à jour (`t`) l'emporte ; à égalité, la boîte la plus haute.
 */
function mergeReview(a, b) {
    const ra = parseJson(a, {}) || {};
    const rb = parseJson(b, {}) || {};
    const out = {};
    for (const skill of [...new Set([...Object.keys(ra), ...Object.keys(rb)])].sort()) {
        const sa = ra[skill] || {};
        const sb = rb[skill] || {};
        out[skill] = {};
        for (const code of [...new Set([...Object.keys(sa), ...Object.keys(sb)])].sort()) {
            const ea = sa[code];
            const eb = sb[code];
            if (!ea || !eb) {
                out[skill][code] = ea || eb;
                continue;
            }
            const ta = ea.t || 0;
            const tb = eb.t || 0;
            out[skill][code] = tb > ta || (tb === ta && (eb.box || 0) > (ea.box || 0)) ? eb : ea;
        }
    }
    return JSON.stringify(out);
}

function mergeValue(key, a, b) {
    // Avant les raccourcis `undefined` : un bestRun présent d'un seul côté doit
    // quand même passer par la sanitisation du chrono.
    if (isBestRunKey(key)) return mergeBestRun(a, b);
    if (isWeekRunKey(key)) return mergeWeekRun(a, b);
    if (a === undefined) return b;
    if (b === undefined) return a;
    if (isNumericKey(key)) return mergeNumeric(a, b);
    if (isExplorationKey(key)) return mergeExploration(a, b);
    if (isDailyResultKey(key)) return mergeDailyResult(a, b);
    if (isStreakKey(key)) return mergeStreak(a, b);
    if (isCountryProgressKey(key)) return mergeCountryProgress(a, b);
    if (isLetterFlagKey(key)) return mergeLetterFlag(a, b);
    if (isLetterRunKey(key)) return mergeLetterRun(a, b);
    if (isPassportKey(key)) return mergePassport(a, b);
    if (isReviewKey(key)) return mergeReview(a, b);
    if (/^\d+$/.test(a) && /^\d+$/.test(b)) return mergeNumeric(a, b);
    return a || b;
}

export function mergeSnapshots(a = {}, b = {}) {
    const out = {};
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if (!isSyncableKey(key)) continue;
        out[key] = mergeValue(key, a[key], b[key]);
    }
    return out;
}
